import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Unlink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ScanQRHeader from '@/components/scan-qr/ScanQRHeader';
import { toast } from 'react-hot-toast';

import successVideo from '@/assets/success.webm';
import loadingVideo from '@/assets/loading.webm';
import errorVideo from '@/assets/error.webm';
import {
    getMagicLinkContents,
    buyPolicyViaLeads,
    checkPaymentStatus,
    type MagicLinkMerchant,
    type MagicLinkProduct,
    type MagicLinkCustomerData,
    type MerchantTerms,
} from '@/service/returnService';

// Razorpay global type
declare global {
    interface Window {
        Razorpay: new (options: RazorpayOptions) => RazorpayInstance;
    }
}

interface RazorpayOptions {
    key: string;
    amount: number;
    currency: string;
    name: string;
    description: string;
    order_id: string;
    handler: (response: RazorpayPaymentResponse) => void;
    prefill?: { name?: string; email?: string; contact?: string };
    theme?: { color?: string; backdrop_color?: string };
    modal?: { ondismiss?: () => void; escape?: boolean; handleback?: boolean; confirm_close?: boolean };
}

interface RazorpayPaymentResponse {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
}

interface RazorpayInstance {
    open: () => void;
    close: () => void;
}

type PlanVariant = 'accidental-damage' | 'extended-warranty';

interface PlanOption {
    label: string;
    price: number;
    months: number;  // policy_term in months
}

// ── Fallback content per variant (generalized terms) ──

const variantContent: Record<PlanVariant, {
    title: string;
    subtitle: string;
    coverageTitle: string;
    coverageItems: string[];
    whyTitle: string;
    whyItems: string[];
    planSectionTitle?: string;
}> = {
    'accidental-damage': {
        title: 'Protect your product from accidental damage',
        subtitle: 'Coverage details and eligibility depend on the selected plan and merchant policy.',
        coverageTitle: 'Coverage may include:',
        coverageItems: [
            'Accidental drops, falls, and impact damage',
            'Liquid spills and water damage',
            'Electrical and power surge damage',
            'Screen or display damage (if applicable)',
            'Free annual professional maintenance (Doorstep Service)',
        ],
        whyTitle: 'Why Add Accidental Care?',
        whyItems: [
            'Helps reduce repair or replacement costs',
            'Easy claim tracking via Protega dashboard',
            'Support handled as per merchant policy',
        ],
        planSectionTitle: 'Choose a protection Care plan',
    },
    'extended-warranty': {
        title: "Extend your product's warranty",
        subtitle: 'Continue coverage for your product after the standard warranty period ends.',
        coverageTitle: 'Coverage may include:',
        coverageItems: [
            'Manufacturing or workmanship defects beyond standard warranty',
            'Mechanical and electrical component failures',
            'Functional issues covered under standard warranty terms',
            'Repairs and part replacements as per warranty policy',
        ],
        whyTitle: 'Why Add Extended Warranty?',
        whyItems: [
            'Extends protection beyond the original warranty',
            'Helps manage repair costs over time',
            'Easy service support through Protega dashboard',
        ],
        planSectionTitle: 'Choose a warranty plan',
    },
};

// Month labels for plan display
const monthLabels: Record<string, string> = {
    '12': '1 Year',
    '24': '2 Year',
    '36': '3 Year',
    '48': '4 Year',
    '60': '5 Year',
    '72': '6 Year',
};

// ── Page component ──────────────────────────────────────────

const MagicPolicyPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [merchant, setMerchant] = useState<MagicLinkMerchant | null>(null);
    const [products, setProducts] = useState<MagicLinkProduct[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<MagicLinkProduct | null>(null);
    const [leadsId, setLeadsId] = useState<string | null>(null);
    const [orderId, setOrderId] = useState<string | null>(null);
    const [customer, setCustomer] = useState<MagicLinkCustomerData | null>(null);
    const [merchantTerms, setMerchantTerms] = useState<MerchantTerms | undefined>(undefined);
    const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState<'idle' | 'loading' | 'success' | 'failed'>('idle');

    // Fetch magic link contents on mount
    const hasFetched = useRef(false);
    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const fetchContents = async () => {
            if (!token) {
                setError('Invalid or missing token');
                setLoading(false);
                return;
            }

            try {
                const response = await getMagicLinkContents(token);
                if (response.success && response.data) {
                    const { merchant: m, productsArray, customerData, leadsId: lid, ordersData } = response.data;
                    setMerchant(m);
                    setProducts(productsArray);
                    setLeadsId(lid);
                    setOrderId(ordersData.id);
                    if (customerData && customerData.length > 0) {
                        setCustomer(customerData[0]);
                    }
                    // Auto-select if only one product
                    if (productsArray.length === 1) {
                        setSelectedProduct(productsArray[0]);
                    }
                    // Parse terms — API may return a JSON string or an object
                    const rawTerms = m.terms;
                    if (rawTerms && typeof rawTerms === 'object') {
                        setMerchantTerms(rawTerms as MerchantTerms);
                    } else if (rawTerms && typeof rawTerms === 'string' && rawTerms.length > 0) {
                        try {
                            setMerchantTerms(JSON.parse(rawTerms) as MerchantTerms);
                        } catch {
                            // terms string wasn't valid JSON — ignore
                        }
                    }
                } else {
                    setError('Link expired or invalid');
                }
            } catch {
                setError('Link expired or invalid');
            } finally {
                setLoading(false);
            }
        };

        fetchContents();
    }, [token]);

    // Determine variant from the selected product's policyType
    const variant: PlanVariant = useMemo(() => {
        const pType = selectedProduct?.policyType;
        if (pType === 'extended') return 'extended-warranty';
        if (pType === 'accidental') return 'accidental-damage';
        // Fallback — check merchant terms keys
        if (merchantTerms?.['Extended Warranty']?.length) return 'extended-warranty';
        return 'accidental-damage';
    }, [selectedProduct, merchantTerms]);

    // Build plans from the product's policyPricing (only non-null entries)
    const plans: PlanOption[] = useMemo(() => {
        if (!selectedProduct?.policyPricing) return [];
        const isEW = variant === 'extended-warranty';
        return Object.entries(selectedProduct.policyPricing)
            .filter(([, price]) => price != null)
            .map(([months, price]) => ({
                label: `${monthLabels[months] ?? months + ' Month'} ${isEW ? 'Extended Warranty' : 'Protection'}`,
                price: price as number,
                months: Number(months),
            }));
    }, [selectedProduct, variant]);

    // Derive content — override coverage with dynamic terms
    const content = useMemo(() => {
        const base = variantContent[variant];

        let coverageItems = base.coverageItems;
        if (merchantTerms) {
            if (variant === 'accidental-damage' && merchantTerms['Accidental Damage']) {
                coverageItems = merchantTerms['Accidental Damage'];
            } else if (variant === 'extended-warranty' && merchantTerms['Extended Warranty']) {
                coverageItems = merchantTerms['Extended Warranty'];
            }
        }

        return { ...base, coverageItems, plans };
    }, [variant, merchantTerms, plans]);

    const handleClose = () => {
        navigate('/');
    };

    const handleContinue = async () => {
        if (!token || !selectedProduct || !merchant || !leadsId || !customer || !orderId) return;

        const selectedPlan = content.plans[selectedPlanIndex];
        if (!selectedPlan) return;

        setIsSubmitting(true);

        try {
            // 1. Create order
            const orderResponse = await buyPolicyViaLeads({
                db_order_id: orderId,
                lead_id: leadsId,
                merchant_id: merchant.id,
                product_id: selectedProduct.id,
                policy_type: selectedProduct.policyType,
                policy_term: selectedPlan.months,
                quantity: 1,
                customer_id: customer.id,
                customer_state: customer.address?.province_code ?? 'MH',
            });

            if (!orderResponse.razorpayOrderID || !orderResponse.razorpayOrderAmount) {
                toast.error('Failed to create order. Please try again.');
                setIsSubmitting(false);
                return;
            }

            const razorpayOrderId = orderResponse.razorpayOrderID;
            const amount = orderResponse.razorpayOrderAmount;
            const leadsTransactionId = orderResponse.leads_transaction_id;
            const currency = 'INR';

            // 2. Open Razorpay checkout
            const options: RazorpayOptions = {
                key: import.meta.env.VITE_RAZORPAY_KEY_ID,
                amount,
                currency,
                name: 'Protega',
                description: selectedPlan.label,
                order_id: razorpayOrderId,
                handler: async () => {
                    setPaymentStatus('loading'); // Show loading state while confirming
                    try {
                        let isPaid = false;
                        let maxRetries = 5;

                        // 3. Poll payment status on backend (webhooks may take a moment)
                        while (maxRetries > 0 && !isPaid) {
                            const checkResponse = await checkPaymentStatus({
                                leads_transaction_id: leadsTransactionId,
                            });

                            if (checkResponse.success && checkResponse.data?.paid) {
                                isPaid = true;
                            } else {
                                maxRetries -= 1;
                                if (maxRetries > 0) {
                                    // Wait 2 seconds before checking again
                                    await new Promise((resolve) => setTimeout(resolve, 2000));
                                } else {
                                    toast.error(checkResponse.message || 'Payment not confirmed yet.');
                                }
                            }
                        }

                        if (isPaid) {
                            setPaymentStatus('success');
                        } else {
                            setPaymentStatus('failed');
                        }
                    } catch {
                        toast.error('Payment verification failed.');
                        setPaymentStatus('failed');
                    }
                },
                prefill: {},
                theme: { color: '#22d3ee', backdrop_color: '#ffffff' }, // attempt fullscreen appearance
                modal: {
                    escape: false,
                    handleback: false,
                    confirm_close: true,
                    ondismiss: () => {
                        setPaymentStatus('failed');
                    },
                },
            };

            const razorpay = new window.Razorpay(options);
            razorpay.open();
            setIsSubmitting(false); // Stop button spinner once modal opens
        } catch {
            toast.error('Something went wrong. Please try again.');
            setIsSubmitting(false);
        }
    };

    // ── Payment status screens ──

    if (loading) {
        return (
            <div className="tw-min-h-screen tw-flex tw-justify-center tw-items-center tw-bg-white">
                <div className="tw-w-12 tw-h-12 tw-border-t-4 tw-border-blue-500 tw-border-solid tw-rounded-full tw-animate-spin"></div>
            </div>
        );
    }

    if (paymentStatus === 'loading') {
        return (
            <div className="tw-min-h-screen tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-white p-6">
                <div className="tw-w-32 tw-h-32 tw-mb-6 tw-flex tw-items-center tw-justify-center">
                    <video
                        src={loadingVideo}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="tw-w-auto tw-h-full tw-object-contain"
                    />
                </div>
                <h2 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-2">Processing Payment...</h2>
                <p className="tw-text-gray-500 tw-text-center">Please do not close or refresh this page.</p>
            </div>
        );
    }

    if (paymentStatus === 'success') {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-white sm:tw-bg-gray-50 sm:tw-p-4">
                <div className="tw-bg-white tw-w-full sm:tw-max-w-md sm:tw-rounded-2xl sm:tw-shadow-xl tw-p-8 tw-min-h-screen sm:tw-min-h-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
                    <div className="tw-w-32 tw-h-32 tw-mb-6 tw-flex tw-items-center tw-justify-center">
                        <video
                            src={successVideo}
                            autoPlay
                            muted
                            playsInline
                            className="tw-w-auto tw-h-full tw-object-contain"
                        />
                    </div>
                    <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Payment Successful!</h2>
                    <p className="tw-text-gray-500 tw-mb-8">Your protection plan is now active. A confirmation email has been sent.</p>
                    <Button onClick={handleClose} className="tw-w-full tw-bg-cyan-500 hover:tw-bg-cyan-600 tw-text-white tw-h-14 tw-rounded-2xl tw-font-medium tw-text-lg">
                        Return Home
                    </Button>
                </div>
            </div>
        );
    }

    if (paymentStatus === 'failed') {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-white sm:tw-bg-gray-50 sm:tw-p-4">
                <div className="tw-bg-white tw-w-full sm:tw-max-w-md sm:tw-rounded-2xl sm:tw-shadow-xl tw-p-8 tw-min-h-screen sm:tw-min-h-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
                    <div className="tw-w-32 tw-h-32 tw-mb-6 tw-flex tw-items-center tw-justify-center">
                        <video
                            src={errorVideo}
                            autoPlay
                            muted
                            playsInline
                            className="tw-w-auto tw-h-full tw-object-contain"
                        />
                    </div>
                    <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Payment Failed</h2>
                    <p className="tw-text-gray-500 tw-mb-8">Your transaction couldn't be completed. No charges were made.</p>
                    <div className="tw-flex tw-flex-col tw-w-full tw-gap-3">
                        <Button onClick={() => setPaymentStatus('idle')} className="tw-w-full tw-bg-cyan-500 hover:tw-bg-cyan-600 tw-text-white tw-h-14 tw-rounded-2xl tw-font-medium tw-text-lg">
                            Try Again
                        </Button>
                        <Button onClick={handleClose} variant="ghost" className="tw-w-full tw-text-gray-500 tw-h-14">
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Error state ──

    if (error || !merchant || products.length === 0) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-white sm:tw-bg-gray-50 sm:tw-p-4">
                <div className="tw-bg-white tw-w-full sm:tw-max-w-md sm:tw-rounded-2xl sm:tw-shadow-xl tw-overflow-hidden tw-relative tw-p-6 sm:tw-p-8 tw-min-h-screen sm:tw-min-h-0 tw-flex tw-flex-col sm:tw-block">
                    <ScanQRHeader onClose={handleClose} />

                    <div className="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-gap-4 tw-pt-10">
                        <div className="tw-w-20 tw-h-20 tw-bg-red-50 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
                            <Unlink className="tw-w-10 tw-h-10 tw-text-red-500" strokeWidth={1.5} />
                        </div>
                        <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900">Link Expired or Invalid</h2>
                        <p className="tw-text-gray-500 tw-text-sm sm:tw-text-base tw-max-w-[280px]">
                            The link you opened seems to be broken or has expired. Please contact support for a new link.
                        </p>

                        <button
                            onClick={handleClose}
                            className="tw-mt-8 tw-w-full tw-py-3 tw-bg-cyan-400 hover:tw-bg-cyan-500 tw-text-white tw-font-semibold tw-rounded-lg tw-transition-colors"
                        >
                            Return to Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Product selection state (multiple products) ──

    if (!selectedProduct && products.length > 1) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-white sm:tw-bg-gray-50 sm:tw-p-4">
                <div className="tw-bg-white tw-w-full sm:tw-max-w-md sm:tw-rounded-2xl sm:tw-shadow-xl tw-overflow-hidden tw-relative tw-p-6 sm:tw-p-8 tw-min-h-screen sm:tw-min-h-0 tw-flex tw-flex-col sm:tw-block">
                    <ScanQRHeader
                        merchantName={merchant.name}
                        merchantLogo={merchant.logo_url ?? undefined}
                        onClose={handleClose}
                    />

                    <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-5 sm:tw-px-4">
                        <div className="tw-text-center tw-mb-1">
                            <h1 className="tw-text-xl sm:tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2 tw-leading-snug">
                                Select a product
                            </h1>
                            <p className="tw-text-gray-500 tw-text-sm sm:tw-text-base">
                                Choose the product you'd like to protect
                            </p>
                        </div>

                        <div className="tw-flex tw-flex-col tw-gap-3">
                            {products.map((prod) => (
                                <button
                                    key={prod.id}
                                    type="button"
                                    onClick={() => setSelectedProduct(prod)}
                                    className="tw-bg-gray-50 tw-rounded-2xl tw-overflow-hidden tw-text-left tw-border-2 tw-border-transparent hover:tw-border-cyan-400 tw-transition-all tw-cursor-pointer tw-outline-none"
                                >
                                    <div className="tw-flex tw-gap-4 tw-p-4">
                                        {prod.image_url ? (
                                            <div className="tw-w-20 tw-h-20 tw-rounded-xl tw-overflow-hidden tw-flex-shrink-0">
                                                <img
                                                    src={prod.image_url}
                                                    alt={prod.title}
                                                    className="tw-w-full tw-h-full tw-object-cover"
                                                />
                                            </div>
                                        ) : (
                                            <div className="tw-w-20 tw-h-20 tw-rounded-xl tw-bg-gray-200 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
                                                <span className="tw-text-gray-400 tw-text-xs">No photo</span>
                                            </div>
                                        )}
                                        <div className="tw-flex tw-flex-col tw-justify-center tw-gap-1.5 tw-min-w-0">
                                            <h3 className="tw-font-semibold tw-text-gray-900 tw-text-sm tw-leading-tight tw-line-clamp-3">
                                                {prod.title || 'Unnamed Product'}
                                            </h3>
                                            {prod.price > 0 && (
                                                <p className="tw-text-sm tw-text-gray-600">
                                                    ₹{prod.price.toLocaleString('en-IN')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Protection plan view (product selected) ──

    if (!selectedProduct) return null;

    const handleGoBack = () => {
        if (products.length > 1) {
            setSelectedProduct(null);
            setSelectedPlanIndex(0);
        } else {
            handleClose();
        }
    };

    return (
        <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-white sm:tw-bg-gray-50 sm:tw-p-4">
            <div className="tw-bg-white tw-w-full sm:tw-max-w-md sm:tw-rounded-2xl sm:tw-shadow-xl tw-overflow-hidden tw-relative tw-p-6 sm:tw-p-8 tw-min-h-screen sm:tw-min-h-0 tw-flex tw-flex-col sm:tw-block">

                <ScanQRHeader
                    merchantName={merchant.name}
                    merchantLogo={merchant.logo_url ?? undefined}
                    onClose={handleClose}
                />

                <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-5 sm:tw-px-4">

                    {/* Header */}
                    <div className="tw-text-center tw-mb-1">
                        <h1 className="tw-text-xl sm:tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2 tw-leading-snug">
                            {content.title}
                        </h1>
                        <p className="tw-text-gray-500 tw-text-sm sm:tw-text-base">
                            {content.subtitle}
                        </p>
                    </div>

                    {/* Selected Product Card */}
                    <div className="tw-bg-gray-50 tw-rounded-2xl tw-overflow-hidden">
                        <div className="tw-flex tw-gap-4 tw-p-4">
                            {selectedProduct.image_url ? (
                                <div className="tw-w-28 tw-h-28 tw-rounded-xl tw-overflow-hidden tw-flex-shrink-0">
                                    <img
                                        src={selectedProduct.image_url}
                                        alt={selectedProduct.title}
                                        className="tw-w-full tw-h-full tw-object-cover"
                                    />
                                </div>
                            ) : (
                                <div className="tw-w-28 tw-h-28 tw-rounded-xl tw-bg-gray-200 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
                                    <span className="tw-text-gray-400 tw-text-xs">No photo</span>
                                </div>
                            )}
                            <div className="tw-flex tw-flex-col tw-justify-center tw-gap-1.5 tw-min-w-0">
                                <h3 className="tw-font-semibold tw-text-gray-900 tw-text-base tw-leading-tight tw-line-clamp-4">
                                    {selectedProduct.title || 'Unnamed Product'}
                                </h3>
                                {selectedProduct.price > 0 && (
                                    <p className="tw-text-sm tw-text-gray-600">
                                        MRP: <span className="tw-font-medium">₹{selectedProduct.price.toLocaleString('en-IN')}</span>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Coverage section */}
                    <div>
                        <h3 className="tw-font-semibold tw-text-gray-900 tw-text-sm tw-mb-2">
                            {content.coverageTitle}
                        </h3>
                        <ul className="tw-space-y-1.5 tw-text-sm tw-text-gray-700">
                            {content.coverageItems.map((item, i) => (
                                <li key={i} className="tw-flex tw-gap-2">
                                    <span className="tw-text-gray-400 tw-flex-shrink-0">•</span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Why section */}
                    <div>
                        <h3 className="tw-font-semibold tw-text-gray-900 tw-text-sm tw-mb-2">
                            {content.whyTitle}
                        </h3>
                        <ul className="tw-space-y-1.5 tw-text-sm tw-text-gray-700">
                            {content.whyItems.map((item, i) => (
                                <li key={i} className="tw-flex tw-gap-2">
                                    <span className="tw-text-gray-400 tw-flex-shrink-0">•</span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Plan selection */}
                    {content.plans.length > 0 && (
                        <div>
                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
                                <h3 className="tw-font-semibold tw-text-gray-900 tw-text-sm">
                                    {content.planSectionTitle}
                                </h3>
                                <span className="tw-text-base">☺</span>
                            </div>
                            <p className="tw-text-[11px] tw-text-gray-400 tw-mb-3">
                                (Free Annual Doorstep Maintenance Service Included)
                            </p>

                            <div className="tw-flex tw-gap-3">
                                {content.plans.map((plan, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => setSelectedPlanIndex(i)}
                                        className={`tw-flex-1 tw-py-3 tw-px-3 tw-rounded-xl tw-text-center tw-cursor-pointer tw-outline-none tw-transition-all tw-border-2
                                            ${selectedPlanIndex === i
                                                ? 'tw-bg-cyan-400 tw-border-cyan-400 tw-text-white'
                                                : 'tw-bg-white tw-border-gray-200 tw-text-gray-900 hover:tw-border-gray-300'
                                            }`}
                                    >
                                        <div className={`tw-font-semibold tw-text-xs tw-mb-0.5 ${selectedPlanIndex === i ? 'tw-text-white' : 'tw-text-gray-900'}`}>
                                            {plan.label}
                                        </div>
                                        <div className={`tw-font-bold tw-text-base ${selectedPlanIndex === i ? 'tw-text-white' : 'tw-text-gray-900'}`}>
                                            ₹{plan.price}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Action buttons */}
                    <div className="tw-mt-auto tw-pb-4 sm:tw-pb-0 tw-flex tw-flex-col tw-items-center tw-gap-3 tw-pt-4">
                        <Button
                            onClick={handleContinue}
                            disabled={isSubmitting}
                            className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-12 tw-bg-cyan-50 hover:tw-bg-cyan-100 tw-text-cyan-600 disabled:tw-text-gray-400 disabled:tw-bg-gray-100 tw-font-semibold tw-text-lg tw-rounded-lg tw-border-none tw-transition-colors"
                        >
                            {isSubmitting && <Loader2 className="tw-w-5 tw-h-5 tw-animate-spin" />}
                            Continue
                        </Button>
                        <button
                            type="button"
                            onClick={handleGoBack}
                            className="tw-text-gray-700 tw-font-medium tw-text-sm tw-bg-transparent tw-border-none tw-cursor-pointer tw-outline-none tw-underline tw-underline-offset-2 hover:tw-text-gray-900 tw-pb-2"
                        >
                            Go back
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default MagicPolicyPage;
